import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, Container, Typography } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';

/** Single merged comparison table (both reference images) */
const ROWS = [
    {
        feature: 'Monthly price',
        standard: '₹7,500',
        plus: '₹12,000',
        premium: '₹18,000',
        enterprise: '₹45,000',
        priceRow: true,
    },
    {
        feature: 'Scheduling & session tracking',
        standard: 'yes',
        plus: 'yes',
        premium: 'yes',
        enterprise: 'yes',
    },
    {
        feature: 'Digital documentation & records',
        standard: 'yes',
        plus: 'yes',
        premium: 'yes',
        enterprise: 'yes',
    },
    {
        feature: 'Consent management (DPDP-aligned)',
        standard: 'Standard forms',
        plus: 'Standard forms',
        premium: 'Standard forms',
        enterprise: 'Multi-language + version control',
        highlight: true,
    },
    {
        feature: 'Therapists included',
        standard: 'First 5; ₹500/staff/month after',
        plus: 'Up to 15; ₹500/staff/month after',
        premium: 'Up to 50; ₹300/staff/month after',
        enterprise: 'Unlimited',
        highlight: true,
    },
    {
        feature: 'Active treatment packages',
        standard: '20 included; ₹500/additional',
        plus: '60 included; ₹500/additional',
        premium: '200 included; ₹300/additional',
        enterprise: 'Unlimited',
        highlight: true,
    },
    {
        feature: 'Payroll calculation',
        standard: 'Add-on — ₹2,000/month',
        plus: 'Included',
        premium: 'Included',
        enterprise: 'Included',
        highlight: true,
    },
    {
        feature: 'Equipment tracking',
        standard: 'Add-on — ₹1,000/month',
        plus: 'yes',
        premium: 'yes',
        enterprise: 'yes',
    },
    {
        feature: 'Operational reports',
        standard: '10/month; ₹500 per 10 additional',
        plus: '50/month; ₹500 per 50 additional',
        premium: 'Unlimited',
        enterprise: 'Unlimited + custom builder',
        highlight: true,
    },
    {
        feature: 'Payment gateway payouts',
        standard: 'no',
        plus: 'yes',
        premium: 'yes',
        enterprise: 'yes',
        highlight: true,
    },
    {
        feature: 'Wearables integration',
        standard: 'no',
        plus: 'no',
        premium: 'yes',
        enterprise: 'yes',
    },
    {
        feature: 'DPDP audit support',
        standard: '₹5,000/quarter',
        plus: '₹5,000/quarter',
        premium: '₹2,500/quarter',
        enterprise: 'Included',
        highlight: true,
    },
    {
        feature: 'Support',
        standard: 'Email; 48hr SLA',
        plus: 'Email; 24hr SLA',
        premium: 'Email + phone; 12hr SLA',
        enterprise: 'Email + phone; 2hr SLA',
        highlight: true,
    },
];

const PLANS = [
    { key: 'standard', label: 'Standard', recommended: false },
    { key: 'plus', label: 'Plus', recommended: true },
    { key: 'premium', label: 'Premium', recommended: false },
    { key: 'enterprise', label: 'Enterprise', recommended: false },
];

function renderCell(value, { priceRow }) {
    if (value === 'yes') {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CheckRoundedIcon
                    sx={{ fontSize: 22, color: '#0066FF' }}
                    aria-label="Included"
                />
            </Box>
        );
    }
    if (value === 'no') {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Typography
                    component="span"
                    sx={{
                        color: 'rgba(15, 23, 42, 0.35)',
                        fontSize: '1.1rem',
                        fontWeight: 500,
                    }}
                >
                    —
                </Typography>
            </Box>
        );
    }
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Typography
                component="span"
                sx={{
                    fontSize: priceRow ? { xs: '0.9375rem', md: '1rem' } : '0.875rem',
                    fontWeight: priceRow ? 700 : 500,
                    color: priceRow ? '#0F172A' : 'rgba(15, 23, 42, 0.82)',
                    lineHeight: 1.5,
                    textAlign: 'center',
                }}
            >
                {value}
            </Typography>
        </Box>
    );
}

const FeatureComparisonSection = () => {
    return (
        <Box
            component="section"
            aria-labelledby="feature-comparison-heading"
            sx={{
                pt: { xs: 5, md: 6.5, lg: 7 },
                pb: { xs: 4, md: 4.5, lg: 4.5 },
                background: 'linear-gradient(180deg, #F0F4FF 0%, #FFFFFF 35%, #FAFBFF 100%)',
                borderTop: '1px solid rgba(0, 102, 255, 0.08)',
            }}
        >
            <Container maxWidth="xl">
                <Box sx={{ maxWidth: 920, mx: 'auto', textAlign: 'center', mb: { xs: 3, md: 4 } }}>
                    <Typography
                        component="span"
                        sx={{
                            display: 'inline-block',
                            fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                            fontWeight: 700,
                            letterSpacing: '0.18em',
                            textTransform: 'uppercase',
                            color: '#0066FF',
                            bgcolor: 'rgba(0, 102, 255, 0.1)',
                            px: { xs: 1.75, sm: 2.25 },
                            py: 0.75,
                            borderRadius: 999,
                            mb: 2,
                        }}
                    >
                        Compare
                    </Typography>
                    <Typography
                        id="feature-comparison-heading"
                        component="h2"
                        sx={{
                            fontSize: { xs: '1.75rem', sm: '2.125rem', md: '2.5rem' },
                            fontWeight: 700,
                            letterSpacing: '-0.03em',
                            lineHeight: 1.15,
                            color: '#111827',
                            m: 0,
                            mb: 2,
                        }}
                    >
                        Full feature comparison.
                    </Typography>
                    <Typography
                        component="p"
                        sx={{
                            m: 0,
                            fontSize: { xs: '0.9375rem', md: '1.0625rem' },
                            lineHeight: 1.7,
                            color: 'rgba(15, 23, 42, 0.72)',
                        }}
                    >
                        Every plan includes a <strong>base allocation</strong>. <strong>Additional therapists</strong> and <strong>treatment packages</strong> are billed at the rates shown below.
                    </Typography>
                </Box>

                <Box
                    sx={{
                        overflowX: 'auto',
                        overflowY: 'visible',
                        borderRadius: '16px',
                        border: '1px solid rgba(15, 23, 42, 0.08)',
                        boxShadow: '0 4px 32px rgba(15, 23, 42, 0.06)',
                        background: '#FFFFFF',
                        WebkitOverflowScrolling: 'touch',
                    }}
                >
                    <Box
                        component="table"
                        sx={{
                            display: 'table',
                            width: '100%',
                            minWidth: 880,
                            borderCollapse: 'separate',
                            borderSpacing: 0,
                            tableLayout: 'auto',
                        }}
                    >
                        <Box component="thead" sx={{ display: 'table-header-group' }}>
                            <Box component="tr" sx={{ display: 'table-row' }}>
                                <Box
                                    component="th"
                                    scope="col"
                                    sx={{
                                        display: 'table-cell',
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 6,
                                        textAlign: 'left',
                                        py: 2.25,
                                        px: 2,
                                        minWidth: 200,
                                        background: 'linear-gradient(180deg, #FAFBFF 0%, #F5F8FF 100%)',
                                        borderBottom: '1px solid rgba(15, 23, 42, 0.1)',
                                        borderRight: '1px solid rgba(15, 23, 42, 0.06)',
                                        fontSize: '0.75rem',
                                        fontWeight: 700,
                                        letterSpacing: '0.12em',
                                        textTransform: 'uppercase',
                                        color: '#6B7280',
                                        verticalAlign: 'bottom',
                                        transition: 'background 0.25s ease',
                                    }}
                                >
                                    {/* Feature */}
                                </Box>
                                {PLANS.map((plan) => (
                                    <Box
                                        key={plan.key}
                                        component="th"
                                        scope="col"
                                        sx={{
                                            display: 'table-cell',
                                            py: 2.25,
                                            px: 1.5,
                                            textAlign: 'center',
                                            verticalAlign: 'bottom',
                                            background: plan.recommended
                                                ? 'linear-gradient(180deg, #EEF4FF 0%, #E0EAFF 100%)'
                                                : 'linear-gradient(180deg, #FAFBFF 0%, #F5F8FF 100%)',
                                            borderBottom: '1px solid rgba(15, 23, 42, 0.1)',
                                            borderLeft: plan.recommended ? '1px solid rgba(0, 102, 255, 0.2)' : 'none',
                                            borderRight: plan.recommended ? '1px solid rgba(0, 102, 255, 0.2)' : 'none',
                                            boxShadow: plan.recommended ? 'inset 0 0 0 1px rgba(0, 102, 255, 0.08)' : 'none',
                                            transition: 'background 0.25s ease',
                                        }}
                                    >
                                        <Typography
                                            component="span"
                                            sx={{
                                                display: 'block',
                                                fontSize: { xs: '0.9375rem', md: '1.0625rem' },
                                                fontWeight: 700,
                                                letterSpacing: '-0.02em',
                                                color: '#111827',
                                            }}
                                        >
                                            {plan.label}
                                        </Typography>
                                        {plan.recommended && (
                                            <Typography
                                                component="span"
                                                sx={{
                                                    display: 'inline-block',
                                                    mt: 1,
                                                    px: 1.25,
                                                    py: 0.35,
                                                    borderRadius: 999,
                                                    fontSize: '0.625rem',
                                                    fontWeight: 700,
                                                    letterSpacing: '0.12em',
                                                    textTransform: 'uppercase',
                                                    color: '#FFFFFF',
                                                    bgcolor: '#0066FF',
                                                }}
                                            >
                                                Recommended
                                            </Typography>
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                        <Box component="tbody" sx={{ display: 'table-row-group' }}>
                            {ROWS.map((row) => (
                                <Box
                                    component="tr"
                                    key={row.feature}
                                    sx={{
                                        display: 'table-row',
                                        transition: 'background-color 0.2s ease',
                                        '&:hover .fc-feature': {
                                            bgcolor: row.highlight ? 'rgba(0, 102, 255, 0.06)' : 'rgba(15, 23, 42, 0.04)',
                                        },
                                        '&:hover .fc-cell': {
                                            bgcolor: row.highlight ? 'rgba(0, 102, 255, 0.04)' : 'rgba(15, 23, 42, 0.03)',
                                        },
                                        '&:hover .fc-cell-plus': {
                                            bgcolor: 'rgba(0, 102, 255, 0.09)',
                                        },
                                    }}
                                >
                                    <Box
                                        component="th"
                                        scope="row"
                                        className="fc-feature"
                                        sx={{
                                            display: 'table-cell',
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 3,
                                            textAlign: 'left',
                                            py: row.priceRow ? 1.5 : 1.75,
                                            px: 2,
                                            fontSize: '0.8125rem',
                                            fontWeight: row.priceRow ? 700 : 600,
                                            color: row.priceRow ? '#111827' : '#374151',
                                            lineHeight: 1.45,
                                            borderBottom: row.priceRow
                                                ? '1px solid rgba(15, 23, 42, 0.1)'
                                                : '1px solid rgba(15, 23, 42, 0.06)',
                                            borderRight: '1px solid rgba(15, 23, 42, 0.06)',
                                            background: row.priceRow
                                                ? 'linear-gradient(180deg, #F5F8FF 0%, #FAFBFF 100%)'
                                                : '#FFFFFF',
                                            boxShadow: '4px 0 12px rgba(15, 23, 42, 0.04)',
                                            transition: 'background-color 0.2s ease',
                                        }}
                                    >
                                        {row.feature}
                                    </Box>
                                    {PLANS.map((plan) => {
                                        const value = row[plan.key];
                                        const isPlus = plan.key === 'plus';
                                        return (
                                            <Box
                                                key={plan.key}
                                                component="td"
                                                className={isPlus ? 'fc-cell fc-cell-plus' : 'fc-cell'}
                                                sx={{
                                                    display: 'table-cell',
                                                    py: row.priceRow ? 1.5 : 1.75,
                                                    px: 1.5,
                                                    textAlign: 'center',
                                                    verticalAlign: 'middle',
                                                    borderBottom: row.priceRow
                                                        ? '1px solid rgba(15, 23, 42, 0.1)'
                                                        : '1px solid rgba(15, 23, 42, 0.06)',
                                                    ...(row.priceRow
                                                        ? {
                                                              background: isPlus
                                                                  ? 'linear-gradient(180deg, #E8EFFF 0%, #EEF4FF 100%)'
                                                                  : 'linear-gradient(180deg, #F5F8FF 0%, #FAFBFF 100%)',
                                                              borderLeft: isPlus
                                                                  ? '1px solid rgba(0, 102, 255, 0.2)'
                                                                  : 'none',
                                                              borderRight: isPlus
                                                                  ? '1px solid rgba(0, 102, 255, 0.2)'
                                                                  : 'none',
                                                          }
                                                        : {
                                                              bgcolor: isPlus
                                                                  ? 'rgba(0, 102, 255, 0.03)'
                                                                  : '#FFFFFF',
                                                          }),
                                                    transition: 'background-color 0.2s ease',
                                                }}
                                            >
                                                {renderCell(value, { priceRow: row.priceRow })}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            ))}
                        </Box>
                        <Box component="tfoot" sx={{ display: 'table-footer-group' }}>
                            <Box component="tr" sx={{ display: 'table-row' }}>
                                <Box
                                    component="td"
                                    sx={{
                                        display: 'table-cell',
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 3,
                                        py: 2.5,
                                        px: 2,
                                        borderTop: '2px solid rgba(15, 23, 42, 0.1)',
                                        background: 'linear-gradient(180deg, #FAFBFF 0%, #FFFFFF 100%)',
                                        fontSize: '0.8125rem',
                                        fontWeight: 600,
                                        color: '#6B7280',
                                        boxShadow: '4px 0 12px rgba(15, 23, 42, 0.04)',
                                    }}
                                >
                                    {/* Next step */}
                                </Box>
                                {PLANS.map((plan) => (
                                    <Box
                                        key={plan.key}
                                        component="td"
                                        sx={{
                                            display: 'table-cell',
                                            py: 2.5,
                                            px: 1.5,
                                            textAlign: 'center',
                                            verticalAlign: 'middle',
                                            borderTop: '2px solid rgba(15, 23, 42, 0.1)',
                                            background:
                                                plan.key === 'plus'
                                                    ? 'linear-gradient(180deg, #F0F5FF 0%, #FFFFFF 100%)'
                                                    : 'linear-gradient(180deg, #FAFBFF 0%, #FFFFFF 100%)',
                                        }}
                                    >
                                        <Button
                                            component={Link}
                                            to="/demo"
                                            variant={plan.key === 'plus' ? 'contained' : 'outlined'}
                                            fullWidth
                                            sx={{
                                                maxWidth: 200,
                                                py: 1.1,
                                                borderRadius: '10px',
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                fontSize: '0.8125rem',
                                                transition:
                                                    'transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease, border-color 0.2s ease',
                                                ...(plan.key === 'plus'
                                                    ? {
                                                          background: 'linear-gradient(135deg, #0066FF, #0052CC)',
                                                          boxShadow: '0 4px 16px rgba(0, 102, 255, 0.25)',
                                                          '&:hover': {
                                                              background: 'linear-gradient(135deg, #0052CC, #003D99)',
                                                              boxShadow: '0 6px 22px rgba(0, 102, 255, 0.32)',
                                                              transform: 'translateY(-1px)',
                                                          },
                                                      }
                                                    : {
                                                          borderWidth: '1.5px',
                                                          borderColor: '#D1D5DB',
                                                          color: '#374151',
                                                          '&:hover': {
                                                              borderColor: '#0066FF',
                                                              color: '#0066FF',
                                                              bgcolor: 'rgba(0, 102, 255, 0.04)',
                                                              transform: 'translateY(-1px)',
                                                          },
                                                      }),
                                            }}
                                        >
                                            Book a demo
                                        </Button>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Container>
        </Box>
    );
};

export default FeatureComparisonSection;
